import { useEffect, useRef, useState } from "react";
import { Images } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { fmt } from "../../i18n/translations";
import { SafeImage } from "./SafeImage";

const PREVIEW = 5;

const Tile = ({ cottage, photo, index, onOpen, overlay, className = "", selected = false, onSelect = null }) => {
  const { t } = useLang();
  const caption = t.captions[photo.caption];
  return (
    <button
      type="button"
      className={`tile ${className} ${selected ? "tile--selected" : ""}`}
      onClick={() => onSelect ? onSelect(index) : onOpen(index)}
      aria-pressed={onSelect ? selected : undefined}
      aria-label={fmt(t.cottages.open, { c: caption })}
      data-testid={`gallery-tile-${photo.id}`}
    >
      <SafeImage src={photo.thumb} alt={`${cottage.name} – ${caption}`} loading="lazy" decoding="async" />
      <span className="tile__caption">{caption}</span>
      {overlay && <span className="tile__more">{overlay}</span>}
    </button>
  );
};

export const Gallery = ({ cottage, onOpen, defaultExpanded = false, rail = false, compact = false, selectedIndex = 0, onSelect = null }) => {
  const { t } = useLang();
  const [all, setAll] = useState(defaultExpanded);
  const railRef = useRef(null);
  const rafRef = useRef(null);
  const speedRef = useRef(0);
  const photos = cottage.photos;
  const preview = photos.slice(1, PREVIEW + 1);
  const rest = photos.length - PREVIEW - 1;

  const stopRail = () => {
    speedRef.current = 0;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const animateRail = () => {
    const node = railRef.current;
    if (!node || speedRef.current === 0) {
      rafRef.current = null;
      return;
    }

    node.scrollLeft += speedRef.current;
    rafRef.current = requestAnimationFrame(animateRail);
  };

  const startRail = (direction) => {
    speedRef.current = direction * 7;
    if (!rafRef.current) rafRef.current = requestAnimationFrame(animateRail);
  };

  const handleCompactHover = (event) => {
    if (!compact || !railRef.current || event.buttons) return;
    const { left, width } = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - left;
    if (x < width * 0.2) startRail(-1);
    else if (x > width * 0.8) startRail(1);
    else stopRail();
  };

  useEffect(() => stopRail, []);

  return (
    <div className={`gallery ${rail ? "gallery--rail" : ""} ${compact ? "gallery--compact" : ""}`} data-testid={`gallery-${cottage.key}`}>
      {!compact && <div className="gallery__head">
        <h4 className="gallery__title">{t.cottages.gallery}</h4>
        <button
          type="button"
          className="btn btn--outline btn--sm"
          aria-expanded={all}
          onClick={() => setAll((a) => !a)}
          data-testid={`gallery-toggle-${cottage.key}`}
        >
          <Images size={16} strokeWidth={1.5} aria-hidden="true" />
          {all ? t.cottages.showLess : fmt(t.cottages.showAll, { n: photos.length })}
        </button>
      </div>}

      {rail && !all ? (
        <div className="gallery__rail-shell" onMouseMove={compact ? handleCompactHover : undefined} onMouseEnter={compact ? handleCompactHover : undefined} onMouseLeave={stopRail}>
          {!compact && <div
            className="gallery__hover-zone gallery__hover-zone--left"
            onMouseEnter={() => startRail(-1)}
            onMouseLeave={stopRail}
            aria-hidden="true"
          />}
          <div
            ref={railRef}
            className="gallery__rail"
            data-testid={`gallery-rail-${cottage.key}`}
          >
            {photos.map((p, i) => (
              <Tile
                key={p.id}
                cottage={cottage}
                photo={p}
                index={i}
                onOpen={onOpen}
                className="tile--rail"
                selected={compact && selectedIndex === i}
                onSelect={compact ? onSelect : null}
              />
            ))}
          </div>
          {!compact && <div
            className="gallery__hover-zone gallery__hover-zone--right"
            onMouseEnter={() => startRail(1)}
            onMouseLeave={stopRail}
            aria-hidden="true"
          />}
        </div>
      ) : all ? (
        <div className="gallery__all" data-testid={`gallery-all-${cottage.key}`}>
          {photos.map((p, i) => (
            <Tile key={p.id} cottage={cottage} photo={p} index={i} onOpen={onOpen} />
          ))}
        </div>
      ) : (
        <div className="gallery__mosaic">
          {preview.map((p, i) => (
            <Tile
              key={p.id}
              cottage={cottage}
              photo={p}
              index={i + 1}
              onOpen={onOpen}
              className={i === 0 ? "tile--lead" : ""}
              overlay={i === preview.length - 1 && rest > 0 ? fmt(t.cottages.more, { n: rest }) : null}
            />
          ))}
        </div>
      )}
    </div>
  );
};
